import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { moneyDisplay } from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DeskDocBackLink } from "@/modules/shell/DeskDocBackLink";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  createSalesInvoice,
  getSalesOrderInvoiceOpen,
  type SalesOrderInvoiceOpenLine,
} from "@/shared/api/sales";
import { notify } from "@/shared/feedback/notify";

export function SalesInvoiceNewPage() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const salesOrderId = searchParams.get("salesOrderId") ?? "";

  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [lines, setLines] = useState<SalesOrderInvoiceOpenLine[]>([]);
  const [qtyByLine, setQtyByLine] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState("");
  const [submitOnCreate, setSubmitOnCreate] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!companySlug || !salesOrderId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getSalesOrderInvoiceOpen(companySlug, salesOrderId)
      .then((d) => {
        if (cancelled) return;
        setOrderNumber(d.order.number);
        setLines(d.lines);
        const init: Record<string, string> = {};
        for (const l of d.lines) {
          init[l.salesOrderLineId] =
            Number(l.qtyRemaining) > 0 ? l.qtyRemaining : "0";
        }
        setQtyByLine(init);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Failed";
        if (msg === "Not signed in") navigate("/login", { replace: true });
        else setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug, salesOrderId, navigate]);

  const billable = useMemo(
    () =>
      lines.filter((l) => {
        const q = Number(qtyByLine[l.salesOrderLineId] ?? 0);
        return q > 0;
      }),
    [lines, qtyByLine],
  );

  async function onCreate() {
    if (!companySlug || !salesOrderId) return;
    if (billable.length === 0) {
      setError("Enter a positive qty on at least one line.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { invoice } = await createSalesInvoice(companySlug, {
        salesOrderId,
        submit: submitOnCreate,
        notes: notes.trim() || undefined,
        lines: billable.map((l) => ({
          productId: l.productId,
          qty: qtyByLine[l.salesOrderLineId]!,
          salesOrderLineId: l.salesOrderLineId,
        })),
      });
      notify.success(
        submitOnCreate
          ? `Invoice ${invoice.number} submitted`
          : `Draft ${invoice.number} saved`,
      );
      navigate(`/c/${companySlug}/sales/invoices/${invoice.id}`, {
        replace: true,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setBusy(false);
    }
  }

  if (!companySlug) return null;

  if (!salesOrderId) {
    return (
      <PageFrame width="narrow" className="py-4">
        <DeskDocBackLink
          listBasePath={`/c/${companySlug}/sales/invoices`}
          listLabel="Sales invoices"
        />
        <PageHeader
          eyebrow="Sales invoice"
          title="New invoice"
          description="Create from an open sales order (standalone invoices come later)."
        />
        <Alert variant="muted" className="mt-4">
          Open a sales order and choose Invoice, or go to{" "}
          <Link
            to={`/c/${companySlug}/sales/orders`}
            className="underline-offset-2 hover:underline"
          >
            Orders
          </Link>
          .
        </Alert>
      </PageFrame>
    );
  }

  return (
    <PageFrame width="narrow">
      <div className="mb-3">
        <Link
          to={`/c/${companySlug}/sales/orders/${salesOrderId}`}
          className="text-sm text-[var(--color-muted)] no-underline hover:text-[var(--color-foreground)] hover:underline"
        >
          ← {orderNumber ? `Order ${orderNumber}` : "Sales order"}
        </Link>
      </div>
      <PageHeader
        eyebrow="Sales invoice"
        title={orderNumber ? `Invoice ${orderNumber}` : "New invoice"}
        description="Bill remaining qty from the order. Leave a line at 0 to skip it."
      />

      {loading && (
        <Alert variant="muted" className="mt-4">
          Loading open-to-invoice…
        </Alert>
      )}
      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}

      {!loading && lines.length === 0 && !error ? (
        <Alert variant="muted" className="mt-4">
          This order has no lines to invoice.
        </Alert>
      ) : null}

      {!loading && lines.length > 0 ? (
        <div className="mt-6 space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[32rem] border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-[var(--color-muted)]">
                  <th className="py-2 pr-3 font-medium">Product</th>
                  <th className="py-2 pr-3 font-medium text-right">Ordered</th>
                  <th className="py-2 pr-3 font-medium text-right">Invoiced</th>
                  <th className="py-2 pr-3 font-medium text-right">Remaining</th>
                  <th className="py-2 font-medium text-right">Bill qty</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr
                    key={l.salesOrderLineId}
                    className="border-b border-[var(--color-border)]/60"
                  >
                    <td className="py-2 pr-3">
                      {l.productName}
                      <div className="font-mono text-xs text-[var(--color-muted)]">
                        {moneyDisplay(l.unitPrice)}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">
                      {l.qtyOrdered}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">
                      {l.qtyInvoiced}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">
                      {l.qtyRemaining}
                    </td>
                    <td className="py-2 text-right">
                      <Input
                        className="ml-auto w-24 text-right font-mono"
                        inputMode="decimal"
                        value={qtyByLine[l.salesOrderLineId] ?? ""}
                        disabled={Number(l.qtyRemaining) <= 0}
                        onChange={(e) =>
                          setQtyByLine((prev) => ({
                            ...prev,
                            [l.salesOrderLineId]: e.target.value,
                          }))
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-2">
            <Label htmlFor="si-notes">Notes</Label>
            <Input
              id="si-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={submitOnCreate}
              onChange={(e) => setSubmitOnCreate(e.target.checked)}
            />
            Submit immediately (open / posted)
          </label>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              disabled={busy || billable.length === 0}
              pending={busy}
              pendingLabel="Creating…"
              onClick={() => void onCreate()}
            >
              {submitOnCreate ? "Create & submit" : "Save draft"}
            </Button>
            <Button asChild type="button" variant="outline" size="sm">
              <Link
                to={`/c/${companySlug}/sales/orders/${salesOrderId}`}
                className="no-underline"
              >
                Cancel
              </Link>
            </Button>
          </div>
        </div>
      ) : null}
    </PageFrame>
  );
}
